// pages/CustomersPage.jsx
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../utils/auth';
import { useNavigate } from 'react-router-dom';
import { customers as customersApi, bookings as bookingsApi, loyaltyApi } from '../utils/api';
import { format } from 'date-fns';

const safeFormat = (val, fmt, opts={}) => {
  if (!val) return '—';
  try {
    const d = typeof val === 'string' ? new Date(val.slice(0,10)+'T12:00:00') : new Date(val);
    if (isNaN(d.getTime())) return '—';
    return format(d, fmt, opts);
  } catch { return '—'; }
};
import { fr } from 'date-fns/locale';
import { Search, Plus, ArrowRight, User, Edit2, X, Trash2, Merge, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import ConfirmModal from '../components/ConfirmModal';

// ── Modale nouveau/édition client ────────────────────────────
function CustomerModal({ customer, onClose, onSaved }) {
  const [form, setForm] = useState({
    firstName: customer?.first_name || '',
    lastName:  customer?.last_name  || '',
    email:     customer?.email      || '',
    phone:     customer?.phone      || '',
    notes:     customer?.notes      || '',
  });
  const [loading, setLoading] = useState(false);
  const f = k => ({ value: form[k], onChange: e => setForm(p => ({ ...p, [k]: e.target.value })) });

  const submit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      if (customer) await customersApi.update(customer.id, form);
      else await customersApi.create(form);
      toast.success(customer ? 'Client mis à jour' : 'Client créé');
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.error || 'Erreur'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <h3 className="modal-title">{customer ? 'Modifier le client' : 'Nouveau client'}</h3>
        <form onSubmit={submit}>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Prénom *</label>
              <input className="input" required {...f('firstName')} placeholder="Jean"/>
            </div>
            <div className="form-group">
              <label className="form-label">Nom *</label>
              <input className="input" required {...f('lastName')} placeholder="Dupont"/>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Email</label>
              <input type="email" className="input" {...f('email')} placeholder="jean@exemple.fr"/>
            </div>
            <div className="form-group">
              <label className="form-label">Téléphone</label>
              <input type="tel" className="input" {...f('phone')} placeholder="06 12 34 56 78"/>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea className="input" rows={3} style={{ resize:'vertical' }} {...f('notes')}
              placeholder="Préférences, anniversaire, groupe habituel…"/>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? <span className="spinner" style={{ width:16, height:16 }}/> : (customer ? 'Mettre à jour' : 'Créer')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Fiche client ─────────────────────────────────────────────
function CustomerDetail({ customer: initialCustomer, onBack, onEdit }) {
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(initialCustomer);
  const [bookings, setBookings] = useState([]);
  const [loyalty, setLoyalty]   = useState(null); // { progress, settings }

  useEffect(() => {
    customersApi.get(initialCustomer.id)
      .then(r => setCustomer(r.data.customer))
      .catch(() => {});
    bookingsApi.list({ customerId: initialCustomer.id })
      .then(r => setBookings(r.data.bookings || []))
      .catch(() => {});
    loyaltyApi.customerStatus(initialCustomer.id)
      .then(r => setLoyalty(r.data))
      .catch(() => {});
  }, [initialCustomer.id]);

  const STATUS_BADGE  = { confirmed:'badge-green', pending:'badge-amber', cancelled:'badge-red', completed:'badge-purple' };
  const STATUS_LABELS = { confirmed:'Confirmé', pending:'En attente', cancelled:'Annulé', completed:'Terminé' };

  return (
    <div>
      {/* En-tête fiche */}
      <div style={{ display:'flex', alignItems:'flex-start', gap:16, marginBottom:24 }}>
        <div style={{ width:56, height:56, borderRadius:'50%', background:'linear-gradient(135deg,var(--accent),var(--teal))',
          display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, fontWeight:700, color:'#fff', flexShrink:0 }}>
          {customer.first_name?.[0]}{customer.last_name?.[0]}
        </div>
        <div style={{ flex:1 }}>
          <h3 style={{ fontSize:20, fontWeight:700, marginBottom:2 }}>
            {customer.first_name} {customer.last_name}
          </h3>
          <div style={{ display:'flex', gap:16, flexWrap:'wrap', fontSize:13, color:'var(--text-2)' }}>
            {customer.email && <span>✉ {customer.email}</span>}
            {customer.phone && <span>📞 {customer.phone}</span>}
          </div>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={() => onEdit(customer)}>
          <Edit2 size={13}/> Modifier
        </button>
      </div>

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:12, marginBottom:20 }}>
        {[
          ['Réservations', customer.booking_count || 0, 'var(--accent-light)'],
          ['Total dépensé', `${parseFloat(customer.total_spent || 0).toFixed(2)} €`, 'var(--teal)'],
          ['Dernière visite', customer.last_booking ? format(new Date(customer.last_booking), 'd MMM yyyy', { locale:fr }) : '—', 'var(--text-1)'],
        ].map(([label, val, color]) => (
          <div key={label} style={{ background:'var(--bg-elevated)', borderRadius:10, padding:'14px 16px' }}>
            <div className="text-xs text-muted" style={{ marginBottom:4 }}>{label}</div>
            <div style={{ fontWeight:700, fontSize:18, color }}>{val}</div>
          </div>
        ))}
      </div>

      {/* Fidélité */}
      {loyalty?.settings?.enabled && (
        <div style={{ background:'rgba(0,210,180,0.08)', border:'1px solid var(--teal)', borderRadius:10,
          padding:'12px 16px', marginBottom:20, display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10 }}>
          <div>
            <div className="text-xs text-muted" style={{ marginBottom:2 }}>Fidélité</div>
            <div style={{ fontWeight:700, fontSize:14, color:'var(--teal)' }}>
              {loyalty.progress.sessions_count || 0} / {loyalty.settings.sessions_required} session(s) avant la prochaine récompense
            </div>
          </div>
          {loyalty.progress.rewards_available > 0 && (
            <span className="badge badge-green" style={{ fontSize:12 }}>
              🎁 {loyalty.progress.rewards_available} récompense{loyalty.progress.rewards_available>1?'s':''} disponible{loyalty.progress.rewards_available>1?'s':''}
            </span>
          )}
        </div>
      )}

      {/* Notes */}
      {customer.notes && (
        <div style={{ background:'var(--bg-elevated)', borderRadius:10, padding:'12px 16px', marginBottom:20 }}>
          <div className="text-xs text-muted" style={{ marginBottom:4 }}>Notes</div>
          <p style={{ margin:0, fontSize:14 }}>{customer.notes}</p>
        </div>
      )}

      {/* Historique réservations */}
      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        <div style={{ padding:'14px 20px', borderBottom:'1px solid var(--border)', fontWeight:600, fontSize:14 }}>
          Historique des réservations
        </div>
        {bookings.length === 0 ? (
          <div className="empty-state" style={{ padding:30 }}><p>Aucune réservation</p></div>
        ) : (
          <div style={{ position:'relative', paddingLeft:24 }}>
            {/* Ligne verticale timeline */}
            <div style={{ position:'absolute', left:8, top:0, bottom:0, width:2,
              background:'var(--border)', borderRadius:2 }}/>

            {bookings.map((b, idx) => {
              const isPast    = new Date(b.session_date) < new Date();
              const statusColor = {
                confirmed:'var(--teal)', pending:'var(--amber)',
                cancelled:'var(--red)', completed:'var(--accent-light)'
              }[b.status] || 'var(--text-3)';
              const amountPaid = parseFloat(b.amount_paid||0);
              const total      = parseFloat(b.total_amount||0);
              const isFullyPaid = amountPaid >= total;
              const isPartial   = amountPaid > 0 && amountPaid < total;

              return (
                <div key={b.id} style={{ position:'relative', marginBottom:12 }}
                  onClick={() => navigate(`/app/reservations/${b.id}`)}>
                  {/* Dot sur la timeline */}
                  <div style={{ position:'absolute', left:-20, top:14,
                    width:12, height:12, borderRadius:'50%', border:'2px solid var(--bg-card)',
                    background: isPast ? 'var(--text-3)' : statusColor,
                    boxShadow: isPast ? 'none' : `0 0 8px ${statusColor}66` }}/>

                  <div style={{ cursor:'pointer', padding:'12px 14px', borderRadius:10,
                    background:'var(--bg-elevated)', border:'1px solid var(--border)',
                    transition:'all 0.1s' }}
                    onMouseEnter={e=>{ e.currentTarget.style.borderColor='var(--accent)'; e.currentTarget.style.background='rgba(124,92,252,0.06)'; }}
                    onMouseLeave={e=>{ e.currentTarget.style.borderColor='var(--border)'; e.currentTarget.style.background='var(--bg-elevated)'; }}>

                    <div style={{ display:'flex', alignItems:'flex-start', gap:10, flexWrap:'wrap' }}>
                      {/* Date + heure */}
                      <div style={{ minWidth:90, flexShrink:0 }}>
                        <div style={{ fontSize:13, fontWeight:700, color: isPast?'var(--text-3)':'var(--text-1)' }}>
                          {safeFormat(b.session_date, 'd MMM yyyy', { locale:fr })}
                        </div>
                        <div style={{ fontSize:11, color:'var(--text-3)', marginTop:1 }}>
                          {b.session_start?.slice(0,5)}
                        </div>
                      </div>

                      {/* Salle + détails */}
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontWeight:700, fontSize:14, marginBottom:2 }}>{b.room_name}</div>
                        <div style={{ fontSize:12, color:'var(--text-3)', display:'flex', gap:10, flexWrap:'wrap' }}>
                          <span>{b.player_count} joueur{b.player_count>1?'s':''}</span>
                          <span className="font-mono" style={{ color:'var(--accent-light)', fontSize:11 }}>{b.booking_ref}</span>
                        </div>
                      </div>

                      {/* Paiement + statut */}
                      <div style={{ textAlign:'right', flexShrink:0 }}>
                        <div style={{ fontWeight:700, fontSize:14, marginBottom:3 }}>
                          {total.toFixed(2)} €
                        </div>
                        <div style={{ display:'flex', gap:4, justifyContent:'flex-end', flexWrap:'wrap' }}>
                          <span className={`badge ${STATUS_BADGE[b.status]||'badge-gray'}`} style={{ fontSize:10 }}>
                            {STATUS_LABELS[b.status]||b.status}
                          </span>
                          {isFullyPaid && (
                            <span className="badge badge-green" style={{ fontSize:10 }}>Soldé</span>
                          )}
                          {isPartial && (
                            <span className="badge badge-amber" style={{ fontSize:10 }}>
                              {amountPaid.toFixed(0)} € payé
                            </span>
                          )}
                          {!isFullyPaid && !isPartial && b.status !== 'cancelled' && (
                            <span className="badge badge-red" style={{ fontSize:10 }}>Impayé</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Page principale ───────────────────────────────────────────

// ── Modale doublons + fusion ──────────────────────────────────
function DuplicatesModal({ onClose, onMerged }) {
  const [groups, setGroups]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [merging, setMerging] = useState(null); // {keepId, mergeId}

  useEffect(() => {
    customersApi.duplicates()
      .then(r => setGroups(r.data.duplicates))
      .catch(() => toast.error('Erreur chargement'))
      .finally(() => setLoading(false));
  }, []);

  const doMerge = async () => {
    if (!merging) return;
    try {
      await customersApi.merge(merging);
      toast.success('Clients fusionnés');
      onMerged();
      // Recharger
      setLoading(true);
      customersApi.duplicates()
        .then(r => setGroups(r.data.duplicates))
        .finally(() => setLoading(false));
      setMerging(null);
    } catch(e) { toast.error(e.response?.data?.error||'Erreur'); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target===e.currentTarget && onClose()}>
      <div className="modal" style={{maxWidth:640}}>
        <h3 className="modal-title">Doublons potentiels</h3>
        <p className="text-sm text-muted" style={{marginBottom:16}}>
          Clients partageant le même nom ou téléphone
        </p>

        {loading && <div className="empty-state"><span className="spinner"/></div>}
        {!loading && groups.length === 0 && (
          <div className="empty-state"><p>✅ Aucun doublon détecté</p></div>
        )}

        {groups.map((group, idx) => (
          <div key={idx} className="card" style={{marginBottom:12,padding:14}}>
            <div className="text-xs text-muted" style={{marginBottom:8,textTransform:'uppercase',letterSpacing:'0.06em'}}>
              {group.name_key}
            </div>
            {group.customers.map(cu => (
              <div key={cu.id} style={{display:'flex',alignItems:'center',gap:10,
                padding:'8px 10px',marginBottom:4,background:'var(--bg-elevated)',borderRadius:8}}>
                <div style={{flex:1}}>
                  <div style={{fontWeight:600,fontSize:13}}>{cu.first_name} {cu.last_name}</div>
                  <div style={{fontSize:11,color:'var(--text-3)'}}>
                    {[cu.email, cu.phone].filter(Boolean).join(' · ')}
                    {' · '}{cu.booking_count} rés.
                  </div>
                </div>
                {merging?.keepId !== cu.id && (
                  <button className="btn btn-secondary btn-sm" style={{fontSize:11}}
                    onClick={() => setMerging(prev =>
                      prev?.keepId ? {...prev, mergeId: cu.id} : {keepId: cu.id}
                    )}>
                    {merging?.keepId ? 'Absorber' : 'Garder'}
                  </button>
                )}
                {merging?.keepId === cu.id && (
                  <span className="badge badge-green" style={{fontSize:10}}>À conserver</span>
                )}
              </div>
            ))}

            {merging?.keepId && merging?.mergeId && (
              <div style={{marginTop:10,display:'flex',gap:8,alignItems:'center'}}>
                <AlertTriangle size={14} color="var(--amber)"/>
                <span className="text-sm text-muted">
                  Les réservations du client absorbé seront transférées. Action irréversible.
                </span>
                <button className="btn btn-primary btn-sm" onClick={doMerge}>
                  Fusionner
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => setMerging(null)}>
                  Annuler
                </button>
              </div>
            )}
          </div>
        ))}

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>
  );
}

export default function CustomersPage() {
  const { can } = useAuth();
  if (!can('customers.view')) return <div className="page-body"><div className="empty-state card"><p>Accès non autorisé</p></div></div>;
  const [customers, setCustomers] = useState([]);
  const [search, setSearch]       = useState('');
  const [loading, setLoading]     = useState(false);
  const [selected, setSelected]   = useState(null);
  const [modal, setModal]         = useState(null);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const removeCustomer = async () => {
    setDeleting(true);
    try {
      await customersApi.remove(confirmDelete.id);
      toast.success('Client supprimé');
      load(search);
      setConfirmDelete(null);
    } catch(e) { toast.error(e.response?.data?.error||'Erreur'); }
    finally { setDeleting(false); }
  };

  const load = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const r = q
        ? await customersApi.search(q)
        : await customersApi.list();
      setCustomers(r.data.customers);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const t = setTimeout(() => load(search), 300);
    return () => clearTimeout(t);
  }, [search, load]);

  return (
    <>
      <div className="page-header">
        <div>
          <h2>Clients</h2>
          <p>Fiches clients, historique des réservations</p>
        </div>
        <div style={{display:'flex',gap:8}}>
          <button className="btn btn-secondary" onClick={() => setShowDuplicates(true)}>
            Doublons potentiels
          </button>
          <button className="btn btn-primary" onClick={() => setModal('new')}>
            <Plus size={15}/> Nouveau client
          </button>
        </div>
      </div>

      <div className="page-body">
        {selected ? (
          // ── Vue détail ──
          <div>
            <button className="btn btn-ghost btn-sm" style={{ marginBottom:20 }}
              onClick={() => setSelected(null)}>
              ← Retour à la liste
            </button>
            <CustomerDetail
              customer={selected}
              onBack={() => setSelected(null)}
              onEdit={c => setModal(c)}
            />
          </div>
        ) : (
          // ── Vue liste ──
          <div>
            <div style={{ position:'relative', maxWidth:400, marginBottom:20 }}>
              <Search size={15} style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:'var(--text-3)' }}/>
              <input className="input" style={{ paddingLeft:36 }}
                placeholder="Rechercher par nom, email, téléphone…"
                value={search} onChange={e => setSearch(e.target.value)}/>
            </div>

            <div className="card" style={{ padding:0, overflow:'hidden' }}>
              {loading ? (
                <div className="empty-state" style={{ padding:40 }}><span className="spinner"/></div>
              ) : customers.length === 0 ? (
                <div className="empty-state" style={{ padding:40 }}>
                  <User size={32} style={{ opacity:0.3 }}/>
                  <p>{search ? 'Aucun client trouvé' : 'Aucun client enregistré'}</p>
                </div>
              ) : (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Client</th><th>Email</th><th>Téléphone</th>
                        <th>Réservations</th><th>Total dépensé</th><th>Dernière visite</th><th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {customers.map(c => (
                        <tr key={c.id} style={{ cursor:'pointer' }} onClick={() => setSelected(c)}>
                          <td>
                            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                              <div style={{ width:32, height:32, borderRadius:'50%',
                                background:'linear-gradient(135deg,var(--accent-glow),var(--teal-glow))',
                                display:'flex', alignItems:'center', justifyContent:'center',
                                fontSize:12, fontWeight:700, flexShrink:0, border:'1px solid var(--border)' }}>
                                {c.first_name?.[0]}{c.last_name?.[0]}
                              </div>
                              <span className="bold">{c.first_name} {c.last_name}</span>
                            </div>
                          </td>
                          <td className="text-sm text-muted">{c.email || '—'}</td>
                          <td className="text-sm">{c.phone || '—'}</td>
                          <td>
                            <span className="badge badge-purple">{c.booking_count || 0}</span>
                          </td>
                          <td className="bold text-teal">{parseFloat(c.total_spent || 0).toFixed(2)} €</td>
                          <td className="text-sm text-muted">
                            {c.last_booking ? format(new Date(c.last_booking), 'd MMM yyyy', { locale:fr }) : '—'}
                          </td>
                          <td>
                      <div style={{display:'flex',gap:4}} onClick={e=>e.stopPropagation()}>
                        <button className="btn btn-ghost btn-sm" style={{padding:4}}
                          onClick={() => setModal(c)}>
                          <Edit2 size={13}/>
                        </button>
                        <button className="btn btn-ghost btn-sm" style={{padding:4}}
                          onClick={() => setConfirmDelete(c)}>
                          <Trash2 size={13} color="var(--red)"/>
                        </button>
                        <ArrowRight size={14} color="var(--text-3)"/>
                      </div>
                    </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {modal && (
        <CustomerModal
          customer={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={() => { load(search); if (selected) setSelected(null); }}
        />
      )}
      {showDuplicates && (
        <DuplicatesModal
          onClose={() => setShowDuplicates(false)}
          onMerged={() => load(search)}
        />
      )}
      {confirmDelete && (
        <ConfirmModal
          title="Supprimer ce client"
          message={`Supprimer ${confirmDelete.first_name} ${confirmDelete.last_name} ? Ses réservations seront conservées.`}
          confirmLabel="Supprimer"
          danger
          loading={deleting}
          onConfirm={removeCustomer}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </>
  );
}
